import CONSTANTS from '../constants'

export type ApplicationInfrastructureType = 'gke' | 'non-gke'
export type TargetResolutionStatus = 'ready' | 'unavailable'
export type ApplicationPortfolio = {
	label: string
	value: string
}
export type ApplicationTargetUnavailableReason =
	| 'missing-project-tags'
	| 'unresolved-deployment'

export type ApplicationTargetResolution = {
	status: TargetResolutionStatus
	reason?: ApplicationTargetUnavailableReason
	message?: string
	supportsGke: boolean
	deployment?: string
	selectedProject?: any
	inheritedTagsID?: string
	inheritedTagDetails?: Record<string, any>
	portfolio?: ApplicationPortfolio
	resolvedCluster?: any
	applicationViewer: string[]
}

const unavailableMessages: Record<ApplicationTargetUnavailableReason, string> =
	{
		'missing-project-tags':
			'The selected GCP Project does not have usable inherited tags.',
		'unresolved-deployment':
			'The selected GCP Project deployment could not be derived.'
	}

const toTrimmedString = (value: unknown): string =>
	typeof value === 'string' ? value.trim() : ''

const isRecord = (value: unknown): value is Record<string, any> =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const normalizePortfolio = (
	value: unknown
): ApplicationPortfolio | undefined => {
	if (isRecord(value)) {
		const label = String(value.label ?? value.value ?? '').trim()
		const optionValue = String(value.value ?? value.label ?? '').trim()

		return label && optionValue ? { label, value: optionValue } : undefined
	}

	const optionValue = String(value ?? '').trim()
	return optionValue ? { label: optionValue, value: optionValue } : undefined
}

export const getProjectPortfolio = (
	project: any,
	inheritedTagDetails?: Record<string, any>
): ApplicationPortfolio | undefined => {
	const details = isRecord(project?.details) ? project.details : {}
	const projectType = toTrimmedString(project?.type)
	const nestedDetails =
		projectType && isRecord(details[projectType]) ? details[projectType] : {}
	const indexedGroup = {
		label: project?.groupLabel,
		value: project?.groupValue
	}

	return [
		details.portfolio,
		nestedDetails.portfolio,
		project?.portfolio,
		inheritedTagDetails?.group,
		project?.group,
		indexedGroup
	]
		.map(normalizePortfolio)
		.find(Boolean)
}

const normalizeViewers = (value: unknown): string[] => {
	if (Array.isArray(value)) {
		return value.map((viewer) => String(viewer ?? '').trim()).filter(Boolean)
	}

	if (typeof value === 'string') {
		return value
			.split(',')
			.map((viewer) => viewer.trim())
			.filter(Boolean)
	}

	return []
}

export const normalizeApplicationInfrastructureType = (
	value: unknown
): ApplicationInfrastructureType | undefined => {
	const normalizedInfrastructureType = String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[\s_-]+/g, '')

	if (normalizedInfrastructureType === 'gke') {
		return 'gke'
	}

	if (normalizedInfrastructureType === 'nongke') {
		return 'non-gke'
	}

	return undefined
}

export const deriveDeployment = (value: unknown): string | undefined => {
	const normalized = String(value ?? '')
		.trim()
		.toLowerCase()
	const deploymentLabel = (() => {
		if (['develop', 'development', 'dev'].includes(normalized)) {
			return 'DEV-PREP'
		}

		if (['staging', 'stage', 'stg'].includes(normalized)) {
			return 'STAGING-PREP'
		}

		if (['production', 'prod', 'prd'].includes(normalized)) {
			return 'PROD-PREP'
		}

		if (['sandbox', 'sbx'].includes(normalized)) {
			return 'SB-PREP'
		}

		return undefined
	})()

	return CONSTANTS.DEPLOYMENT_LIST.find(
		(option) => option.label === deploymentLabel
	)?.value
}

const getProjectInfrastructureType = (
	project: any
): ApplicationInfrastructureType | undefined => {
	const details = isRecord(project?.details) ? project.details : {}

	return normalizeApplicationInfrastructureType(details.infrastructureType)
}

const getApprovedClustersForProject = (project: any, platforms: any[] = []) => {
	const projectName = toTrimmedString(project?.tags?.details?.projectName)

	if (!projectName) {
		return []
	}

	return platforms.filter(
		(platform) =>
			platform?.gcpProjectID === projectName &&
			platform?.status === 'APPROVED'
	)
}

const getClusterViewers = (cluster: any): string[] => {
	const details = isRecord(cluster?.details) ? cluster.details : {}
	const viewerSource =
		cluster?.platformViewer ??
		details.platformViewer ??
		details.platformViewers ??
		details.applicationViewer ??
		details.viewers

	return normalizeViewers(viewerSource)
}

const getUnavailableResolution = (
	reason: ApplicationTargetUnavailableReason,
	resolution: Partial<ApplicationTargetResolution> = {}
): ApplicationTargetResolution => ({
	status: 'unavailable',
	reason,
	message: unavailableMessages[reason],
	applicationViewer: [],
	supportsGke: false,
	...resolution
})

export const resolveApplicationTarget = (
	project: any,
	platforms: any[] = []
): ApplicationTargetResolution => {
	const selectedProject = project
	const platformList = Array.isArray(platforms) ? platforms : []
	const inheritedTagsID =
		toTrimmedString(project?.tagsID) || toTrimmedString(project?.tags?.tagsID)
	const inheritedTagDetails = isRecord(project?.tags?.details)
		? project.tags.details
		: undefined
	const portfolio = getProjectPortfolio(project, inheritedTagDetails)

	if (!inheritedTagsID || !inheritedTagDetails) {
		return getUnavailableResolution('missing-project-tags', {
			selectedProject,
			portfolio
		})
	}

	//FETCHES ALL APPROVED CLUSTERS FOR A SPECIFIC CLUSTER (In theory this should be only 1)
	const approvedClusters = getApprovedClustersForProject(project, platformList)
	const projectInfrastructureType = getProjectInfrastructureType(project)
	const supportsGke =
		projectInfrastructureType === 'gke' || approvedClusters.length > 0
	const resolvedCluster =
		approvedClusters.length === 1 ? approvedClusters[0] : undefined
	//AUTO FILL THE DEPLOYMENT BASED ON THE ENVIRONMENT
	const deployment = deriveDeployment(project?.type)

	if (!deployment) {
		return getUnavailableResolution('unresolved-deployment', {
			selectedProject,
			supportsGke,
			inheritedTagsID,
			inheritedTagDetails,
			portfolio,
			resolvedCluster
		})
	}

	return {
		status: 'ready',
		supportsGke,
		deployment,
		selectedProject,
		inheritedTagsID,
		inheritedTagDetails,
		portfolio,
		resolvedCluster,
		applicationViewer: resolvedCluster
			? getClusterViewers(resolvedCluster)
			: normalizeViewers(project?.details?.projectViewerDistributionList)
	}
}
